import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Activity, ArrowRight } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";

const BG_IMG =
  "https://images.pexels.com/photos/36595248/pexels-photo-36595248.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function LoginPage() {
  const { user, initialized, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("shloime@dialoghealthcare.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (initialized && user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-[#F7F5F0]">
      {/* Left visual */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src={BG_IMG}
          alt="Dialog Healthcare"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#292723]/55" />
        <div className="relative h-full flex flex-col justify-between p-10 text-[#F7F5F0]">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-[#4A6B5D] grid place-items-center">
              <Activity className="h-5 w-5" />
            </div>
            <div className="heading font-semibold text-lg tracking-tight">Dialog Healthcare</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] opacity-70 mb-3">Endocrinology Operations</div>
            <h1 className="heading text-4xl font-bold leading-tight max-w-md">
              A quiet, careful record of every practitioner, facility, and visit.
            </h1>
            <p className="mt-4 text-sm opacity-80 max-w-md">
              Manage your Nurse Practitioners, their assigned facilities, patient loads and visit coding — all in one place.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <div className="h-8 w-8 rounded-md bg-[#4A6B5D] grid place-items-center text-white">
                <Activity className="h-4 w-4" />
              </div>
              <span className="heading font-semibold">Dialog Healthcare</span>
            </div>
            <div className="text-xs uppercase tracking-[0.15em] text-[#8A847A] mb-2">Admin sign in</div>
            <h2 className="heading text-3xl sm:text-4xl font-bold text-[#292723]">Welcome back.</h2>
            <p className="text-sm text-[#5C5850] mt-2">Sign in to manage the team.</p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11 bg-white border-[#E5E0D8]"
                autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11 bg-white border-[#E5E0D8]"
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 bg-[#4A6B5D] hover:bg-[#3E594D] text-white active:scale-[0.98] transition-transform"
            >
              {loading ? "Signing in..." : (<><span>Sign in</span><ArrowRight className="ml-2 h-4 w-4" /></>)}
            </Button>
          </form>

          <p className="mt-8 text-xs text-[#8A847A]">
            Protected internal tool. Access is limited to authorized staff.
          </p>
        </div>
      </div>
    </div>
  );
}
